import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { applyColor }from '~src/style/ColorService';

import { Messages } from '../../../codegen/Localize';
import { cssClass } from '../../../style';
import { fetchCapacityInfoForInfoBar, fetchSiteScopedCapacityInfoForInfoBar } from '../../../userManagement/services/licenses/LicensesService';
import { ILicenseConsumptionInfo } from '../../serverApi/types/ILicenseConsumptionInfo';

export interface LicenseInfoBarProps {
  readonly hasSiteLicensingInfo: boolean;
}

export function LicenseInfoBar(props: LicenseInfoBarProps) {
  const [isFetching, setIsFetching] = React.useState(true);
  const [licenseInfo, setLicenseInfo] = React.useState<ILicenseConsumptionInfo>(undefined);
  const [isCoreBasedLicense, setIsCoreBasedLicense] = React.useState(false);
  const [isUnlimitedViewer, setIsUnlimitedLicense] = React.useState(false);


  React.useEffect(
    () => {
      let didUnmount = false;
      const fetch = async () => {
        const licenseInfoPromise = props.hasSiteLicensingInfo ?
          fetchSiteScopedCapacityInfoForInfoBar() :
          fetchCapacityInfoForInfoBar();
        const licenseInfo = await licenseInfoPromise;
        if (!didUnmount) {
          setIsFetching(false);
          setLicenseInfo(licenseInfo);
          setIsCoreBasedLicense(licenseInfo.totalCoreLicenses > 0);
          setIsUnlimitedLicense(licenseInfo.totalViewerUsers === 99999999);

        }
      };

      fetch();

      return () => {
        didUnmount = true;
      };
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function getViewerInfo(){
    if(isCoreBasedLicense){
        return Messages.licenseViewerUnlimited();
    } else if(isUnlimitedViewer) {
        return Messages.licenseViewerUnlimitedWithCounts({
          USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedViewerUsers)});
    } else {
      return Messages.licenseViewerCounts({
        USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedViewerUsers),
        TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalViewerUsers)
      });
    }
}

  const showIntegrationUsers = FeatureFlags.uiamIntegrationUserLicenseEnabled
    && getSessionManagementServiceInstance().isOnlineEnvironment();

  function getIntegrationInfo() {
    const usedRaw = _.isUndefined(licenseInfo) || _.isUndefined(licenseInfo.usedIntegrationUsers)
      ? 0
      : licenseInfo.usedIntegrationUsers;
    return Messages.licenseIntegrationUnlimitedWithCounts({ USED_COUNT_LOC: L10n.localizedCount(usedRaw) });
  }


  return (
    <div className={LicenseInfoBarContainer} data-tb-test-id='license-info-bar'>
      { !isFetching &&
      <dl style={{ display: 'flex', flexWrap: 'wrap' }}>
        <dd className={LicenseInfoBarItem}>
          {Messages.licenseCreatorCounts({
            USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedCreatorUsers),
            TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalCreatorUsers)
          })}
        </dd>
        <dd className={LicenseInfoBarItem}>
          {isCoreBasedLicense ? Messages.licenseExplorerUnlimited() :
            Messages.licenseExplorerCounts({
            USED_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.usedExplorerUsers),
            TOTAL_COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalExplorerUsers)
          })}
        </dd>
        <dd className={LicenseInfoBarItem}>
        {getViewerInfo()}
        </dd>
        {showIntegrationUsers &&
          <dd className={LicenseInfoBarItem} data-tb-test-id='license-integration-counts'>
            {getIntegrationInfo()}
          </dd>}
        <dd className={LicenseInfoBarItem}>
          {Messages.unlicensedCount({
            COUNT_LOC: _.isUndefined(licenseInfo) ? '' : L10n.localizedCount(licenseInfo.totalUnlicensedUsers)
          })}
        </dd>
      </dl>}
    </div>
  );
}

const cssPrefix = 'LicenseInfoBar';
const LicenseInfoBarContainer = cssClass(cssPrefix, 'container', {
  height: 'auto',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  paddingTop: '4px',
  $nest: {
    '&>*': {
      paddingRight: TabStyles.Spacing.MediumSpace,
      paddingLeft: TabStyles.Spacing.MediumSpace,
    },
    '&>*:last-child': {
      paddingRight: 0,
    },
    '&>*:first-child': {
      paddingLeft: 0,
    }
  }
});

const LicenseInfoBarItem = cssClass(cssPrefix, 'item', {
  minWidth: 0, // 0 is the default
  color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.Grey102),
  fontSize: '14px',
  marginInlineStart: 0,
  paddingRight: TabStyles.Spacing.ExtraLargeSpace,
});
